import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  type AuthUser,
  type LoginInput,
  type RegisterInput,
  getMe,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  changePassword as apiChangePassword,
} from '../api/auth';

interface RegisterResult {
  pendingApproval: boolean;
  requiresEmailVerification?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  forcePasswordChange: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  handlePasswordChange: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const response = await getMe();
      if (response.success && response.user) {
        setUser(response.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      await refreshUser();
      setIsLoading(false);
    };
    initAuth();
  }, [refreshUser]);

  const login = useCallback(async (input: LoginInput) => {
    const response = await apiLogin(input);
    if (response.success && response.user) {
      setUser(response.user);
      // Check if password change is required
      if (response.forcePasswordChange) {
        setForcePasswordChange(true);
      }
    } else {
      throw new Error(response.error || 'Errore durante il login');
    }
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<RegisterResult> => {
    const response = await apiRegister(input);
    if (response.success) {
      // If pending approval, don't set user (no session was created)
      if (response.pendingApproval) {
        return {
          pendingApproval: true,
          requiresEmailVerification: response.requiresEmailVerification || false,
        };
      }
      // Otherwise, user was auto-approved (first user)
      if (response.user) {
        setUser(response.user);
      }
      return { pendingApproval: false };
    } else {
      throw new Error(response.error || 'Errore durante la registrazione');
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setForcePasswordChange(false);
  }, []);

  const handlePasswordChange = useCallback(async (currentPassword: string, newPassword: string) => {
    const response = await apiChangePassword(currentPassword, newPassword);
    if (response.success) {
      setForcePasswordChange(false);
    } else {
      throw new Error(response.error || 'Errore durante il cambio password');
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    forcePasswordChange,
    login,
    register,
    logout,
    refreshUser,
    handlePasswordChange,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
